import { OFFER_TYPE_HOTEL } from '~/consts/offerTypes';
import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';

import { createOpenAIClient } from '~/utils/openaiClient';
import { reportError } from '~/utils/reportError';

import { getRoomRateLabel } from './utils';

interface AutofillPackageResult extends Record<string, unknown> {
  name: string;
  fk_room_type_id: string;
  fk_room_rate_id: string;
  package_options: Array<{ fk_room_rate_id: string; name: string }>;
  room_occupancy: string;
  flexible_nights: boolean;
  status: App.Package['status'];
  regions: Array<string>;
  number_of_nights: number;
  max_extra_nights: number;
}

type PackageWithAccommodationFields = App.Package & {
  max_extra_nights?: number;
  number_of_nights?: number;
};

const LEGACY_AUTOFILL_ROOM_OCCUPANCY = 'Two Adults';
export const LEGACY_AUTOFILL_NUMBER_OF_NIGHTS = 1;
export const LEGACY_AUTOFILL_MAX_EXTRA_NIGHTS = 29;
const LEGACY_AUTOFILL_REGIONS = ['world'] as const;

const ROOM_TYPE_MATCH_MODEL = 'gpt-4.1-mini';

const ROOM_TYPE_MATCH_RESPONSE_SCHEMA = {
  type: 'object',
  properties: {
    matchedRoomTypeName: { type: ['string', 'null'] },
  },
  required: ['matchedRoomTypeName'],
  additionalProperties: false,
};

function parseRoomTypeMatchResponse(content: string): string | null {
  try {
    const parsed = JSON.parse(content);
    return typeof parsed?.matchedRoomTypeName === 'string' ? parsed.matchedRoomTypeName : null;
  } catch (parseError) {
    reportError(parseError);
    return null;
  }
}

function buildPackageOptions(roomRates: Array<App.RoomRate>): AutofillPackageResult['package_options'] {
  return roomRates.map((roomRate) => ({
    fk_room_rate_id: roomRate.id,
    name: getRoomRateLabel(roomRate),
  }));
}

// The fields every autofill sets; callers add number_of_nights/max_extra_nights with their own defaulting.
export function buildAutofillPackageFields(roomType: App.RoomType, roomRates: Array<App.RoomRate>) {
  return {
    name: roomType.name,
    fk_room_type_id: roomType.id,
    fk_room_rate_id: roomRates[0].id,
    package_options: buildPackageOptions(roomRates),
    room_occupancy: LEGACY_AUTOFILL_ROOM_OCCUPANCY,
    flexible_nights: true,
    // AI-suggested link; keep the package in draft until a CM verifies the room type and approves it.
    status: 'draft' as const,
    regions: [...LEGACY_AUTOFILL_REGIONS],
  };
}

export function filterFlashRoomRates(roomRates: Array<App.RoomRate>): Array<App.RoomRate> {
  return roomRates.filter((rate) => rate.rate_plan.product_type === ROOM_RATE_FLASH_TYPE);
}

// A deal option named e.g. "One King Standard" is a *substring* match for both "One King Standard" and
// "One King Standard Harbour View", so naive string matching silently prefers whichever room type happens to
// be the shortest textual hit. It's also normal for several deal options to legitimately share one room type
// (different length-of-stay/occupancy variants), so "the room type isn't taken yet" isn't a safe disambiguator
// either. An LLM call gives a best-effort suggestion instead of a guaranteed-wrong string match; the CM still
// reviews the linked room type (and package status) before approving the package.
export async function matchPackageRoomTypeWithAi({
  offerPackage,
  packageName,
  property,
}: {
  offerPackage: App.Package;
  packageName?: string;
  property?: App.Property;
}): Promise<App.RoomType | undefined> {
  const roomTypes = property?.room_types ?? [];
  const sourcePackageName = (packageName ?? offerPackage.deal_option_name ?? offerPackage.name ?? '').trim();

  if (!roomTypes.length || !sourcePackageName) {
    return undefined;
  }

  try {
    const client = createOpenAIClient();
    const response = await client.responses.create({
      model: ROOM_TYPE_MATCH_MODEL,
      input: [
        {
          role: 'system',
          content: [
            'Match an accommodation deal option name to the single room type it belongs to, from a fixed list of room types at the property.',
            'Room type names often overlap, e.g. "One King Standard" and "One King Standard Harbour View" - only pick the longer, more specific name when the deal option name itself contains that extra detail. Otherwise prefer the shorter, exact name.',
            'Several deal options can legitimately belong to the same room type - they may differ by length of stay or occupancy - so a room type being a likely match elsewhere is not a reason to avoid it.',
            'Only match when confident; otherwise use null.',
          ].join('\n'),
        },
        {
          role: 'user',
          content: [
            `Deal option name: ${sourcePackageName}`,
            `Room types at this property: ${JSON.stringify(roomTypes.map((roomType) => roomType.name))}`,
          ].join('\n'),
        },
      ],
      text: {
        format: {
          type: 'json_schema',
          name: 'room_type_match',
          schema: ROOM_TYPE_MATCH_RESPONSE_SCHEMA,
          strict: true,
        },
      },
    });

    const matchedName = parseRoomTypeMatchResponse(response.output_text || '');

    return matchedName ? roomTypes.find((roomType) => roomType.name === matchedName) : undefined;
  } catch (error) {
    reportError(error);
    return undefined;
  }
}

export async function buildAutofillPackageValues({
  offerPackage,
  property,
  offerType,
}: {
  offerPackage: App.Package;
  property?: App.Property;
  offerType?: App.OfferType;
}): Promise<AutofillPackageResult | null> {
  if (!property?.room_types?.length || offerType !== OFFER_TYPE_HOTEL) {
    return null;
  }

  const roomType = await matchPackageRoomTypeWithAi({ offerPackage, property });

  if (!roomType) {
    return null;
  }

  const roomRates = filterFlashRoomRates(roomType.room_rates ?? []);
  const packageFields = offerPackage as PackageWithAccommodationFields;

  if (roomRates.length === 0) {
    return null;
  }

  return {
    ...buildAutofillPackageFields(roomType, roomRates),
    number_of_nights: packageFields.number_of_nights ?? LEGACY_AUTOFILL_NUMBER_OF_NIGHTS,
    max_extra_nights: packageFields.max_extra_nights ?? LEGACY_AUTOFILL_MAX_EXTRA_NIGHTS,
  };
}
